import React, { useCallback, useEffect, useState } from 'react';
import { useKioskFlow } from './hooks/useKioskFlow';
import { usePreventNavigation } from './hooks/usePreventNavigation';
import { useIdleTimer } from './hooks/useIdleTimer';
import { IDLE_TIMEOUT_MS } from './utils/config';
import { LanguageProvider, useLanguage } from './i18n/LanguageContext';
import { WelcomePage } from './pages/WelcomePage';
import { MenuSelectionPage } from './pages/MenuSelectionPage';
import { PhoneInputPage } from './pages/PhoneInputPage';
import { PartySizePage } from './pages/PartySizePage';
import { CompletePage } from './pages/CompletePage';
import { PublicWaitingPage } from './pages/PublicWaitingPage';
import { DemoPreviewOverlay } from './components/DemoPreviewOverlay';
import { DemoTakeoutToggle } from './components/DemoTakeoutToggle';
import { KioskLogin } from './components/KioskLogin';
import { KioskLogoutGesture } from './components/KioskLogoutGesture';
import { getStoreKioskSettings } from './api/waiting';
import { getPublicStatus, PublicWaitingStatus } from './api/publicWaiting';
import { getStoreImage } from './api/storeImage';
import { track } from './utils/track';

const STORAGE_KEY_KIOSK_STORE = 'kiosk_store_id';
const STORAGE_KEY_KIOSK_LICENSE = 'kiosk_license_key';
const STORAGE_KEY_KIOSK_LOGIN_ID = 'kiosk_login_id';

/**
 * URL에서 공개 대기 현황 페이지인지 판단
 * - /wait/:storeId 경로
 * - ?view=public&storeId=xxx 쿼리 파라미터
 * 공개 페이지면 storeId 반환, 아니면 null
 */
function getPublicWaitingStoreId(): string | null {
  // 1. /wait/:storeId 경로 확인
  const pathMatch = window.location.pathname.match(/^\/wait\/([^/]+)/);
  if (pathMatch) {
    return decodeURIComponent(pathMatch[1]);
  }

  // 2. ?view=public&storeId=xxx 쿼리 파라미터 확인
  const params = new URLSearchParams(window.location.search);
  if (params.get('view') === 'public') {
    const storeId = params.get('storeId');
    if (storeId) {
      return storeId;
    }
  }

  return null;
}

/** 키오스크 모드 (기존 로직) */
function KioskApp({
  storeId,
  isDemo = false,
  onLogout,
}: {
  storeId: string;
  isDemo?: boolean;
  onLogout?: () => void;
}) {
  const { language, resetLanguage } = useLanguage();
  const [state, actions] = useKioskFlow(storeId, language, isDemo);
  const [takeoutEnabled, setTakeoutEnabled] = useState(isDemo); // 데모 모드는 포장 ON으로 시작
  const [publicStatus, setPublicStatus] = useState<PublicWaitingStatus | null>(null);
  const [storeImage, setStoreImage] = useState<string | null>(null);

  // 매장 설정 로드 (포장 활성화 여부)
  useEffect(() => {
    if (isDemo) return;
    getStoreKioskSettings(storeId).then((s) => {
      setTakeoutEnabled(s.takeoutEnabled);
    });
  }, [storeId, isDemo]);

  // 매장 이미지 로드 (한 번만, 60초마다 갱신)
  useEffect(() => {
    if (isDemo) return;
    let cancelled = false;
    const fetchImage = () => {
      getStoreImage(storeId).then((res) => {
        if (!cancelled) setStoreImage(res.storeImage);
      });
    };
    fetchImage();
    const interval = setInterval(fetchImage, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [storeId, isDemo]);

  // 웨이팅 현황 polling (30초 간격)
  useEffect(() => {
    if (isDemo) return;
    let cancelled = false;
    const fetchStatus = () => {
      getPublicStatus(storeId)
        .then((status) => {
          if (!cancelled) setPublicStatus(status);
        })
        .catch(() => {
          // 네트워크 오류 무시 (기본값 0 유지)
        });
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [storeId, isDemo]);

  const layoutProps = {
    takeoutEnabled,
    waitingCount: publicStatus?.currentWaiting ?? 0,
    estimatedMinutes: publicStatus?.estimatedWaitMinutes ?? 0,
    takeoutWaitingCount: publicStatus?.takeoutWaiting ?? 0,
    takeoutEstimatedMinutes: publicStatus?.takeoutEstimatedMinutes ?? 0,
    storeImage,
    isAcceptingNow: publicStatus?.isAcceptingNow ?? true,
    closedMessage: publicStatus?.closedMessage ?? null,
  };

  // 네비게이션 방지 (뒤로가기, 새로고침, F5 등) - 데모 모드에서는 비활성화
  usePreventNavigation(!isDemo);

  // 초기화 시 언어도 한국어로 리셋
  const handleIdle = useCallback(() => {
    resetLanguage();
    actions.reset();
  }, [resetLanguage, actions]);

  // 미입력 시 자동 초기화 (웰컴 화면 + 데모 모드에서는 비활성화)
  useIdleTimer({
    timeoutMs: IDLE_TIMEOUT_MS,
    onIdle: handleIdle,
    enabled: state.currentPage !== 'welcome' && !isDemo,
  });

  // 완료 후 리셋 시 언어도 초기화
  const handleReset = useCallback(() => {
    resetLanguage();
    actions.reset();
  }, [resetLanguage, actions]);

  // 데모 모드에서 포장 ON/OFF 토글 (현재 진행 중이던 플로우는 초기화)
  const handleToggleTakeout = useCallback((next: boolean) => {
    setTakeoutEnabled(next);
    resetLanguage();
    actions.reset();
  }, [resetLanguage, actions]);

  // 포장: 전화번호 입력 후 바로 제출, 매장: 인원수 선택
  const handlePhoneNext = useCallback(() => {
    if (state.orderType === 'TAKEOUT') {
      actions.setPartySize(1);
      setTimeout(() => actions.submit(), 50);
    } else {
      actions.goToPartySize();
    }
  }, [state.orderType, actions]);

  const renderPage = (): React.ReactElement | null => {
    switch (state.currentPage) {
      case 'welcome':
        return (
          <WelcomePage
            {...layoutProps}
            onStartDineIn={() => { actions.setOrderType('DINE_IN'); actions.goToPhone(); }}
            onStartTakeout={() => { actions.setOrderType('TAKEOUT'); actions.goToMenu(); }}
            onStart={actions.goToPhone}
          />
        );

      case 'orderType':
        // 별도 페이지 불필요 (WelcomePage에 통합됨), 방어 코드
        actions.goToPhone();
        return null;

      case 'menu':
        return (
          <MenuSelectionPage
            {...layoutProps}
            storeId={storeId}
            orderItems={state.orderItems}
            onSetQuantity={actions.setItemQuantity}
            onNext={actions.goToPhone}
            onBack={actions.goToWelcome}
            isDemo={isDemo}
          />
        );

      case 'phone':
        return (
          <PhoneInputPage
            {...layoutProps}
            phone={state.phone}
            onDigit={actions.appendDigit}
            onDelete={actions.deleteDigit}
            onClear={actions.clearPhone}
            onNext={handlePhoneNext}
            onBack={
              state.orderType === 'TAKEOUT'
                ? actions.goToMenu
                : takeoutEnabled
                  ? actions.goToWelcome
                  : undefined
            }
          />
        );

      case 'partySize':
        return (
          <PartySizePage
            {...layoutProps}
            partySize={state.partySize}
            isSubmitting={state.isSubmitting}
            error={state.error}
            onSelect={actions.setPartySize}
            onSubmit={actions.submit}
          />
        );

      case 'complete':
        if (!state.result) {
          // result가 없으면 리셋 (방어 코드)
          handleReset();
          return null;
        }
        return (
          <CompletePage
            result={state.result}
            onReset={handleReset}
            isDemo={isDemo}
            storeId={storeId}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      {renderPage()}
      {isDemo && (
        <DemoTakeoutToggle
          takeoutEnabled={takeoutEnabled}
          onToggle={handleToggleTakeout}
        />
      )}
      {!isDemo && onLogout && <KioskLogoutGesture onLogout={onLogout} />}
    </>
  );
}

/** ?demo=1 데모 모드인지 판단 */
function isDemoMode(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.get('demo') === '1';
}

function App() {
  // 1. 공개 대기 현황 페이지 (로그인 없이 표시) — 최상단에서 먼저 처리
  const publicStoreId = getPublicWaitingStoreId();
  if (publicStoreId) {
    return <PublicWaitingPage storeId={publicStoreId} />;
  }

  // 2. ?demo=1 데모 모드 (저장 없이 메모리 미리보기)
  if (isDemoMode()) {
    return <DemoKioskApp />;
  }

  // 3. 로그인 세션이 있으면 키오스크, 없으면 로그인 화면
  return <KioskAppWrapper />;
}

/** ?demo=1 데모 미리보기 */
function DemoKioskApp() {
  const [showDemoOverlay, setShowDemoOverlay] = useState(true);
  useEffect(() => {
    track('kiosk_demo'); // 미리보기(데모) 진입 추적 (1회)
  }, []);
  return (
    <LanguageProvider>
      <KioskApp storeId="demo-preview" isDemo={true} />
      {showDemoOverlay && (
        <DemoPreviewOverlay onClose={() => setShowDemoOverlay(false)} />
      )}
    </LanguageProvider>
  );
}

/** 로그인 세션(localStorage)을 기준으로 키오스크/로그인 화면을 분기 */
function KioskAppWrapper() {
  const [storeId, setStoreId] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEY_KIOSK_STORE),
  );

  const handleLoggedIn = useCallback((id: string) => {
    setStoreId(id);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY_KIOSK_STORE);
    localStorage.removeItem(STORAGE_KEY_KIOSK_LICENSE);
    localStorage.removeItem(STORAGE_KEY_KIOSK_LOGIN_ID);
    setStoreId(null);
  }, []);

  if (!storeId) {
    return <KioskLogin onLoggedIn={handleLoggedIn} />;
  }

  return (
    <LanguageProvider>
      <KioskApp storeId={storeId} onLogout={handleLogout} />
    </LanguageProvider>
  );
}

export default App;
